import { Text, TextLink } from '@oneaudi/unified-web-components';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { isDisclaimerAvailable } from '~/utils/date';

export const CardContent = ({
	children,
	alwaysVisible,
	linksSection,
	onDescriptionToggle,
	disclaimerHeadline,
	disclaimerContent,
	disclaimerStartDate,
	disclaimerEndDate,
}: {
	children?: ReactNode | ReactNode[];
	linksSection?: ReactNode | ReactNode[];
	alwaysVisible: boolean;
	onDescriptionToggle?: (value: boolean) => void;
	disclaimerHeadline?: string;
	disclaimerContent?: string;
	disclaimerStartDate?: string;
	disclaimerEndDate?: string;
}) => {
	const { i18n } = useI18n();
	const [showDescription, setShowDescription] = useState(alwaysVisible);

	useEffect(() => {
		setShowDescription(alwaysVisible);
	}, [alwaysVisible]);

	const toggleDescription = useCallback(
		(event: MouseEvent) => {
			event.stopPropagation();
			event.preventDefault();

			setShowDescription(!showDescription);
			if (onDescriptionToggle) {
				onDescriptionToggle(showDescription);
			}
		},
		[onDescriptionToggle, showDescription],
	);

	const reactNodesAvailable = (nodes: ReactNode | ReactNode[]) =>
		(Array.isArray(nodes) && nodes.filter(Boolean).length > 0) || (!Array.isArray(nodes) && !!nodes);

	const renderBox = reactNodesAvailable(children);

	const renderLinks = reactNodesAvailable(linksSection);

	return renderBox ? (
		<>
			<Collapsible open={showDescription} onOpenChange={setShowDescription}>
				{!alwaysVisible && (
					<CollapsibleTrigger asChild>
						<TextLink
							onClick={toggleDescription}
							variant="standalone"
							size="sm"
							icon={showDescription ? 'arrow-up' : 'arrow-down'}
							iconPosition="trailing">
							{showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails')}
						</TextLink>
					</CollapsibleTrigger>
				)}
				<CollapsibleContent className="collapsible-content">
					{/* eslint-disable-next-line */}
					<div tabIndex={0}>
						<Text
							margin={[alwaysVisible ? 0 : '12px', '0', '0', '0']}
							className="content-container"
							tag="div"
							variant={{ type: 'copy', order: '2', style: 'normal' }}>
							{children}
						</Text>
					</div>
					{renderLinks ? (
						<div onClick={(event) => event.stopPropagation()} className="selection-card-description">
							{linksSection}
						</div>
					) : null}
					{disclaimerContent && isDisclaimerAvailable(disclaimerStartDate, disclaimerEndDate) && (
						<div className="disclaimer-wrapper">
							<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
						</div>
					)}
				</CollapsibleContent>
			</Collapsible>
		</>
	) : renderLinks ? (
		<>
			<div onClick={(event) => event.stopPropagation()} className="selection-card-description">
				{linksSection}
				{disclaimerContent && isDisclaimerAvailable(disclaimerStartDate, disclaimerEndDate) && (
					<div className="disclaimer-wrapper">
						<Disclaimer type="item" headline={disclaimerHeadline} content={disclaimerContent} />
					</div>
				)}
				{/* TODO: check
				 {disclaimer?.disclaimerText &&
					isDisclaimerAvailable(disclaimer.disclaimerStartDate, disclaimer.disclaimerEndDate) && (
						<div className="disclaimer-wrapper">
							<Disclaimer
								type="item"
								headline={disclaimer.disclaimerHeadline}
								content={disclaimer.disclaimerText}
							/>
						</div>
					)} */}
			</div>
		</>
	) : null;
};
